import { moduleShadedFraction } from "./shadow.js";
const DEG = Math.PI / 180;
const AFFECTED_THRESHOLD = 0.05;
// ---------------------------------------------------------------------------
// Sonnenstände
// ---------------------------------------------------------------------------
/**
 * Repräsentative Sonnenstände (Nordhalbkugel, AT) für die Nahschatten-
 * Vorbemessung: Tag-Äquinoktium (Deklination 0°) + Wintersonnwende
 * (Deklination −23.44°, worst case Nahschatten), je 09/12/15 Uhr Sonnenzeit.
 *
 * Berechnet Elevation/Azimut deterministisch aus Breite und Stundenwinkel
 * (Standard-Astronomieformeln). Es werden NUR Tagstunden (elevation > 0)
 * zurückgegeben. Gewichte: Mittag höher als Morgen/Nachmittag, Äquinoktium
 * höher als Winter (mehr Jahresertrag in der Übergangszeit).
 */
export function representativeSunSamples(latitudeDeg) {
    const lat = latitudeDeg * DEG;
    // Deklination + Saison-Gewicht.
    const seasons = [
        { name: "Äquinoktium", declDeg: 0, seasonWeight: 1.0 },
        { name: "Wintersonnwende", declDeg: -23.44, seasonWeight: 0.6 },
    ];
    // Sonnenzeit-Stunden → Stundenwinkel (15°/h ab Mittag; vormittags negativ).
    const hours = [
        { label: "09h", hour: 9, hourWeight: 0.8 },
        { label: "12h", hour: 12, hourWeight: 1.0 },
        { label: "15h", hour: 15, hourWeight: 0.8 },
    ];
    const out = [];
    for (const s of seasons) {
        const decl = s.declDeg * DEG;
        for (const h of hours) {
            const H = (h.hour - 12) * 15 * DEG; // Stundenwinkel (rad)
            // Elevation: sin(el) = sin(lat)·sin(decl) + cos(lat)·cos(decl)·cos(H)
            const sinEl = Math.sin(lat) * Math.sin(decl) + Math.cos(lat) * Math.cos(decl) * Math.cos(H);
            const elevationDeg = Math.asin(clamp(sinEl, -1, 1)) / DEG;
            if (!(elevationDeg > 0))
                continue; // nur Tagstunden
            // Azimut (von Nord, im Uhrzeigersinn) robust per atan2:
            //   Ost-Komponente  = −cos(decl)·sin(H)
            //   Nord-Komponente = sin(decl)·cos(lat) − cos(decl)·sin(lat)·cos(H)
            // atan2(Ost, Nord) liefert den Azimut ab Nord im Uhrzeigersinn.
            const sinH = Math.sin(H);
            const cosH = Math.cos(H);
            const east = -Math.cos(decl) * sinH;
            const north = Math.sin(decl) * Math.cos(lat) - Math.cos(decl) * Math.sin(lat) * cosH;
            let azimuthDeg = (Math.atan2(east, north) / DEG) % 360;
            if (azimuthDeg < 0)
                azimuthDeg += 360;
            out.push({
                label: `${s.name} ${h.label}`,
                azimuthDeg,
                elevationDeg,
                weight: s.seasonWeight * h.hourWeight,
            });
        }
    }
    return out;
}
// ---------------------------------------------------------------------------
// Hauptfunktion
// ---------------------------------------------------------------------------
export function computeNearShadow(input) {
    const shadedThreshold = input.shadedThreshold ?? 0.5;
    const modules = (input.modules ?? []).filter((m) => m && m.wM > 0 && m.hM > 0);
    const obstacles = (input.obstacles ?? []).filter((o) => o && o.widthM > 0 && o.depthM > 0 && o.heightM > 0);
    const samples = (input.sun ?? representativeSunSamples(input.latitudeDeg)).filter((s) => s && s.elevationDeg > 0 && s.weight > 0);
    // Triviale Fälle: keine Verschattung möglich.
    if (modules.length === 0 || obstacles.length === 0 || samples.length === 0) {
        return {
            perModule: modules.map((m) => ({
                row: m.row,
                col: m.col,
                xM: m.xM,
                yM: m.yM,
                wM: m.wM,
                hM: m.hM,
                shadedFraction: 0,
            })),
            shadedModuleCount: 0,
            affectedModuleCount: 0,
            nearShadingLossPct: 0,
            worstSampleLabel: "",
            worstShadowPolys: [],
        };
    }
    // Dach-Ebenen-Achsen (ENU) für die Projektion.
    const t = input.roofTiltDeg * DEG;
    const A = input.roofAzimuthDeg * DEG;
    const axes = roofPlaneAxes(t, A);
    // Pro Modul: gewichtete Summe der shadedFraction über gültige Samples.
    const weightedSum = new Array(modules.length).fill(0);
    let validWeightTotal = 0;
    // Pro Sample: Gesamtverschattung (Summe Modul-Anteile) → worst sample.
    let worstLabel = "";
    let worstTotal = -1;
    let worstSampleIndex = -1;
    const validSamples = [];
    samples.forEach((sample, sIdx) => {
        const polys = shadowPolysForSample(obstacles, sample, axes);
        if (polys === null)
            return; // s·n <= 0 → Eigenverschattung, ausschließen
        validSamples.push({ sample, index: sIdx });
        validWeightTotal += sample.weight;
        let sampleTotal = 0;
        const polyList = polys.map((p) => p.points);
        modules.forEach((m, mIdx) => {
            const frac = maxShadedFraction(m, polyList);
            weightedSum[mIdx] += frac * sample.weight;
            sampleTotal += frac;
        });
        if (sampleTotal > worstTotal) {
            worstTotal = sampleTotal;
            worstLabel = sample.label;
            worstSampleIndex = sIdx;
        }
    });
    // Keine gültigen Samples (alle s·n <= 0) → keine Direkterträge zu verschatten.
    if (validSamples.length === 0 || validWeightTotal <= 0) {
        return {
            perModule: modules.map((m) => ({
                row: m.row,
                col: m.col,
                xM: m.xM,
                yM: m.yM,
                wM: m.wM,
                hM: m.hM,
                shadedFraction: 0,
            })),
            shadedModuleCount: 0,
            affectedModuleCount: 0,
            nearShadingLossPct: 0,
            worstSampleLabel: "",
            worstShadowPolys: [],
        };
    }
    // Modul-Anteile über gültige Samples renormieren.
    const perModule = modules.map((m, i) => ({
        row: m.row,
        col: m.col,
        xM: m.xM,
        yM: m.yM,
        wM: m.wM,
        hM: m.hM,
        shadedFraction: weightedSum[i] / validWeightTotal,
    }));
    const shadedModuleCount = perModule.filter((m) => m.shadedFraction >= shadedThreshold).length;
    const affectedModuleCount = perModule.filter((m) => m.shadedFraction > AFFECTED_THRESHOLD).length;
    // nearShadingLossPct: flächengewichteter Mittelwert der Modul-Anteile × 100.
    let areaWeighted = 0;
    let areaTotal = 0;
    for (const m of perModule) {
        const area = m.wM * m.hM;
        areaWeighted += m.shadedFraction * area;
        areaTotal += area;
    }
    const nearShadingLossPct = areaTotal > 0 ? (areaWeighted / areaTotal) * 100 : 0;
    // Schattenpolygone für genau den worst sample (für das SVG-Overlay).
    const worstShadowPolys = worstSampleIndex >= 0
        ? (shadowPolysForSample(obstacles, samples[worstSampleIndex], axes) ?? [])
        : [];
    return {
        perModule,
        shadedModuleCount,
        affectedModuleCount,
        nearShadingLossPct,
        worstSampleLabel: worstLabel,
        worstShadowPolys,
    };
}
/**
 * Dach-Ebenen-Achsen in ENU (Ost, Nord, Up) für Neigung t (rad) um Azimut A (rad).
 *   n  = [sin(t)·sin(A), sin(t)·cos(A), cos(t)]
 *   eu = [cos(A), −sin(A), 0]            (entlang Traufe, horizontal)
 *   ev = n × eu                          (Falllinie, in der Ebene aufwärts)
 * {eu, ev, n} bildet ein Rechtssystem (eu × ev = n); für t = 0 ist eu ≈ Ost,
 * ev ≈ Nord.
 */
export function roofPlaneAxes(t, A) {
    const n = {
        x: Math.sin(t) * Math.sin(A),
        y: Math.sin(t) * Math.cos(A),
        z: Math.cos(t),
    };
    const eu = { x: Math.cos(A), y: -Math.sin(A), z: 0 };
    const ev = cross(n, eu);
    return { n: normalize(n), eu: normalize(eu), ev: normalize(ev) };
}
/** Sonnen-Einheitsvektor in ENU. az im Uhrzeigersinn ab Nord. */
function sunVectorENU(sample) {
    const el = sample.elevationDeg * DEG;
    const az = sample.azimuthDeg * DEG;
    const cosEl = Math.cos(el);
    return {
        x: cosEl * Math.sin(az), // Ost
        y: cosEl * Math.cos(az), // Nord
        z: Math.sin(el), // Up
    };
}
/**
 * In-Ebenen-Schattenversatz (du, dv) in Dach-Metern für einen Punkt der Höhe h
 * über der Ebene, oder null wenn s·n <= 0 (Sonne hinter der Dachfläche).
 *
 *   versatz = −(h / (s·n)) · (s − (s·n)·n)     (Projektion entlang s auf die Ebene)
 *   du = versatz · eu ;  dv = versatz · ev
 */
function inPlaneShadowOffset(s, h, axes) {
    const sn = dot(s, axes.n);
    if (!(sn > 0))
        return null; // Eigenverschattung / Sonne in der Ebene
    // Komponente von s parallel zur Ebene (s minus Normalanteil), skaliert mit h/sn.
    const factor = -h / sn;
    const offset = {
        x: factor * (s.x - sn * axes.n.x),
        y: factor * (s.y - sn * axes.n.y),
        z: factor * (s.z - sn * axes.n.z),
    };
    return {
        du: dot(offset, axes.eu),
        dv: dot(offset, axes.ev),
    };
}
/**
 * Schattenpolygone aller Hindernisse für einen Sonnenstand (Dach-Ebenen-Meter).
 * Liefert null, wenn s·n <= 0 (kein Direktertrag → Sample auszuschließen).
 */
function shadowPolysForSample(obstacles, sample, axes) {
    const s = sunVectorENU(sample);
    const off = inPlaneShadowOffset(s, 1, axes); // Test mit h = 1; null = ausgeschlossen
    if (off === null)
        return null;
    const polys = [];
    for (const o of obstacles) {
        const poly = shadowPolygonForObstacle(o, s, axes);
        if (poly.length >= 3) {
            polys.push({ obstacleId: o.id, points: poly });
        }
    }
    return polys;
}
/**
 * Schattenpolygon EINES Rechteck-Hindernisses: konvexe Hülle aus den 4 Footprint-
 * Ecken (Basis, Höhe 0) + den 4 um (du, dv) für h = heightM versetzten Oberkanten-
 * Ecken. Ebenen-agnostisch, gleicher Frame wie GridModuleRect.
 */
function shadowPolygonForObstacle(o, s, axes) {
    const off = inPlaneShadowOffset(s, o.heightM, axes);
    if (off === null)
        return [];
    const x0 = o.xM;
    const y0 = o.yM;
    const x1 = o.xM + o.widthM;
    const y1 = o.yM + o.depthM;
    const corners = [
        { x: x0, y: y0 },
        { x: x1, y: y0 },
        { x: x1, y: y1 },
        { x: x0, y: y1 },
    ];
    const pts = [];
    for (const c of corners) {
        pts.push({ x: c.x, y: c.y }); // Footprint-Basis
        pts.push({ x: c.x + off.du, y: c.y + off.dv }); // projizierte Oberkante
    }
    return convexHull(pts);
}
/** Maximaler Verschattungsanteil eines Moduls über alle Hindernis-Polygone. */
function maxShadedFraction(m, polys) {
    let max = 0;
    for (const poly of polys) {
        const f = moduleShadedFraction({ xM: m.xM, yM: m.yM, wM: m.wM, hM: m.hM }, poly);
        if (f > max)
            max = f;
        if (max >= 1)
            return 1;
    }
    return max;
}
// ---------------------------------------------------------------------------
// kleine Vektor-/Geometrie-Helfer
// ---------------------------------------------------------------------------
function clamp(v, lo, hi) {
    return Math.max(lo, Math.min(hi, v));
}
function dot(a, b) {
    return a.x * b.x + a.y * b.y + a.z * b.z;
}
function cross(a, b) {
    return {
        x: a.y * b.z - a.z * b.y,
        y: a.z * b.x - a.x * b.z,
        z: a.x * b.y - a.y * b.x,
    };
}
function normalize(v) {
    const len = Math.hypot(v.x, v.y, v.z) || 1;
    return { x: v.x / len, y: v.y / len, z: v.z / len };
}
/** Konvexe Hülle (Andrew's monotone chain), CCW, ohne doppelten Schlusspunkt. */
function convexHull(points) {
    const pts = points.slice().sort((p, q) => (p.x === q.x ? p.y - q.y : p.x - q.x));
    const uniq = [];
    for (const p of pts) {
        const last = uniq[uniq.length - 1];
        if (!last || Math.abs(last.x - p.x) > 1e-12 || Math.abs(last.y - p.y) > 1e-12) {
            uniq.push(p);
        }
    }
    if (uniq.length < 3)
        return uniq;
    const crossP = (o, a, b) => (a.x - o.x) * (b.y - o.y) - (a.y - o.y) * (b.x - o.x);
    const lower = [];
    for (const p of uniq) {
        while (lower.length >= 2 && crossP(lower[lower.length - 2], lower[lower.length - 1], p) <= 0) {
            lower.pop();
        }
        lower.push(p);
    }
    const upper = [];
    for (let i = uniq.length - 1; i >= 0; i--) {
        const p = uniq[i];
        while (upper.length >= 2 && crossP(upper[upper.length - 2], upper[upper.length - 1], p) <= 0) {
            upper.pop();
        }
        upper.push(p);
    }
    lower.pop();
    upper.pop();
    return lower.concat(upper);
}
//# sourceMappingURL=near-shadow.js.map